import { TweenLite } from "gsap";
import * as PIXI from 'pixi.js';
import { Application, Texture } from 'pixi.js';
import { AudioManager, Track } from '../AudioManager';
import { IInterval } from '../Context/Interval';
import { ISystemContext } from '../Context/SystemContext';
import { ITimeout } from '../Context/Timeout';
import { CreateElement, Delay, Log, LogGroup, Safe, Scale, SetStyles, Size } from '../MyHelpers';
import { DecodedImageResource, ImageResource } from "../ResourceManager/ImageResource";
import { ResourceIdentifier } from '../ResourceManager/ResourceIdentifier';
import { IReadOnlyResourceManager, IWriteOnlyResourceManager, ResourceManager } from '../ResourceManager/ResourceManager';
import { Resourceful } from '../ResourceManager/Resourceful';
import { Settings } from '../Settings';
import { Translator } from '../Translator';
import { ZIndex } from '../ZIndex';
import { Card, CardColor, DoubleUpCollectAllAction, DoubleUpCollectHalfAction, DoubleUpConfiguration, DoubleUpEndAction, DoubleUpGameState, DoubleUpPickAction, DoubleUpStartAction, FrontendErrorCode, HistoryGame, IDoubleUpAction, Money, SimpleSlotGameState } from '../api';
import { service } from '../services';
import { Bonus } from './Bonus/Bonus';
import { cardMap, soundMap } from './DoubleUpMapResources';
import { ActionStats, IHistoryGame } from './IHistoyGame';
import { StateChange, StateMachine } from './StateMachine';
import { Button } from "../IO";
import { RobotPlay } from "../RobotPlay";
import { IBackendSettingsService } from "../../services/BackendSettingsService";

/** Contenedor PIXI con nombre para debug/organización. */
class MyPIXIContainer extends PIXI.Container {
	constructor(name: string) {
		super();
		this.name = name;
	}
}

type DisplayedCard = Card | null;

export interface DoubleUpParams {
    gameName: string,
    translator: Translator,
    systemContext: ISystemContext,
    viewSize: Size,
    resid: ResourceIdentifier,
    audioManager: AudioManager,
	logGroup: LogGroup,
    robotPlay: RobotPlay,
}
enum DoubleUpStates{
    Idle = "Idle",
    StartGame = "StartGame",
    Playing = "Playing",
    WaitResult = "WaitResult", 
    ShowPick = "ShowPick",
    EndGame = "EndGame",
    RecoverState = "RecoverState",
    CollectHalf = "CollectHalf",
    ShowCollectHalf = "ShowCollectHalf",
    CollectAll = "CollectAll",
    ShowCollectAll = "ShowCollectAll"
    // WinState ="WinState",
    // LoseState = "LoseState"
}

export class DoubleUp implements Resourceful, IHistoryGame {


    protected systemContext: ISystemContext = null;

    /** Contenedor PIXI raíz (como Buy Bonus); se añade al reels container en el slot. */
    protected doubleUpContainer: PIXI.Container = null;

    protected anim: TweenLite = null;

    protected viewSize: Size = null;

    protected translator: Translator = null;

    protected lastCardContainer: PIXI.Container = null;
    protected controlsContainer: PIXI.Container = null;
    protected gameViewContainer: PIXI.Container = null;
    protected winLayoutContainer: PIXI.Container = null
    protected loseLayoutContainer: PIXI.Container = null;
    protected noGameSign: PIXI.Container = null;
    protected redSingContainer: PIXI.Container = null;
    protected blackSingContainer: PIXI.Container = null;
    protected collecHalfContainerTxt: PIXI.Container = null;
    protected collectAllContainerTxt: PIXI.Container = null;
    protected betLimitContainerTxt: PIXI.Container = null;
    protected betAndWinContainer:PIXI.Container = null;




    
    protected winText: PIXI.Text = null;
    protected loseText: PIXI.Text = null;
    protected collectHalfTxt: PIXI.Text = null;
    protected collectAllTxt: PIXI.Text = null;
    protected betLimitTxt:PIXI.Text = null;
    protected showBigLastCard: boolean;

    protected nextPickDelay: ITimeout = null;
    protected collectHalfDelay: ITimeout = null;
    protected collectAllDelay: ITimeout = null;



    private stateMachine: StateMachine<DoubleUpStates>;

    protected gameName: string = "GenericGame";

    //protected path: string = Settings.ResourcesFolder + "multigame/slot/common/resources/DoubleUpFeature"
    protected gameResourcesPath: string;
    protected cardsPath: string = Settings.ResourcesFolder + "multigame/slot/common/resources/DoubleUpFeature/cards/";
    protected soundsPath: string = Settings.ResourcesFolder + "multigame/slot/common/resources/DoubleUpFeature/sounds/";

    protected header: string = null;
    protected resid: ResourceIdentifier = null;
    protected historyCardsContTitle: PIXI.Container = null;
    protected lastCardContainerTitle: PIXI.Container = null;
    protected doubleUpConfiguration: DoubleUpConfiguration = null;
    protected audioManager: AudioManager = null;
    protected doubleParams: DoubleUpParams = null;
    


    private logGroup: LogGroup = null;
    protected readonly HEIGHT = 900;

    protected robotPlay: RobotPlay;
    protected restoreWebGlInterval : IInterval = null;
    protected backendSettings:IBackendSettingsService = null;
    protected isSmallView: boolean = false;

    protected winLimit: number = null;

    public constructor(doubleUpParams: DoubleUpParams, doubleUpConfiguration: DoubleUpConfiguration, backendFromSettings: IBackendSettingsService) {

        this.backendSettings = backendFromSettings;
        this.doubleUpConfiguration = doubleUpConfiguration;
        this.doubleParams = doubleUpParams;
        this.audioManager = doubleUpParams.audioManager;
        this.winLimit = this.backendSettings?.GetNum("play.doubleuplimit", 100) ?? 100;

        this.logGroup = doubleUpParams.logGroup.Add("DoubleUp");

        this.systemContext = doubleUpParams.systemContext.CreateSubContext("DoubleUpFeature");
        this.robotPlay = doubleUpParams.robotPlay;

        this.nextPickDelay = this.systemContext.TimingContext().CreateTimeout("nextPickDelay", 2000, () => this.NextPickDelayed());
        this.collectHalfDelay = this.systemContext.TimingContext().CreateTimeout("collectHalfDelay", 2000, () => this.onShowCollectHalfDelayedEnd());
        this.collectAllDelay = this.systemContext.TimingContext().CreateTimeout("collectAllDelay", 2000, () => this.onShowCollectAllDelayedEnd());

        this.stateMachine = new StateMachine<DoubleUpStates>(DoubleUpStates.Idle, this.logGroup, (s) => s as string);
        this.stateMachine.SetStateChangeCallback((old, newState, cb) => console.log("State changed to " + newState));

        this.stateMachine.SetCallback(DoubleUpStates.StartGame, StateChange.Begin, () => this.OnStart());
        this.stateMachine.SetCallback(DoubleUpStates.RecoverState, StateChange.Begin, () => this.OnRecoverBegin());

        this.stateMachine.SetCallback(DoubleUpStates.Playing, StateChange.Begin, () => this.OnPlayingBegin());
        this.stateMachine.SetCallback(DoubleUpStates.Playing, StateChange.End, () => this.OnPlayingEnd());

        this.stateMachine.SetCallback(DoubleUpStates.ShowPick, StateChange.Begin, () => this.OnShowPickBegin());
        this.stateMachine.SetCallback(DoubleUpStates.ShowPick, StateChange.End, () => this.OnShowPickEnd());

        this.stateMachine.SetCallback(DoubleUpStates.CollectHalf, StateChange.Begin, () => this.onCollectHalfBegin());
        this.stateMachine.SetCallback(DoubleUpStates.CollectHalf, StateChange.End, () => this.onCollectHalfEnd());
        this.stateMachine.SetCallback(DoubleUpStates.ShowCollectHalf, StateChange.Begin, () => this.onShowCollectHalfBegin());
        this.stateMachine.SetCallback(DoubleUpStates.ShowCollectHalf, StateChange.End, () => this.onShowCollectHalfEnd());

        this.stateMachine.SetCallback(DoubleUpStates.CollectAll, StateChange.Begin, () => this.onCollectAllBegin());
        this.stateMachine.SetCallback(DoubleUpStates.CollectAll, StateChange.End, () => this.onCollectAllEnd());
        this.stateMachine.SetCallback(DoubleUpStates.ShowCollectAll, StateChange.Begin, () => this.onShowCollectAllBegin());
        this.stateMachine.SetCallback(DoubleUpStates.ShowCollectAll, StateChange.End, () => this.onShowCollectAllEnd());

        this.stateMachine.SetCallback(DoubleUpStates.EndGame, StateChange.Begin, () => this.OnEndBegin());

        this.viewSize = doubleUpParams.viewSize;
        this.translator = doubleUpParams.translator;
        this.gameName = doubleUpParams.gameName;
        this.resid = doubleUpParams.resid.Add("DoubleUpFeature")

        this.gameResourcesPath = Settings.ResourcesFolder + "multigame/slot/" + this.gameName + "/resources/";
        this.showBigLastCard = Settings.ShowLastBigCard;

        console.log("juego: ", doubleUpParams.gameName)

        this.restoreWebGlInterval = this.systemContext.TimingContext().CreateInterval("restoreWebGlInterval", 500, () => {
			let success = this.tryRecoverWebglContext();

			// success, stop timer
			if (!success)
			{
				this.recoverFailCount++;
				if (this.recoverFailCount == this.maxRecoverFailCount)
				{
				    void service.api?.frontendError(FrontendErrorCode.WebGLRecoverFail, `${this.resid.ID()} in ${this.recoverFailCount} tries`);
				}
			}
		});

        //CreateElement("div", {
        //    classes: ["doubleUpBackground"],
        //    parentToAppend: this.view,
        //    style: {
        //        width: this.viewSize.width + "px",
        //        height: Scale.Hpx(610),
        //        position: "absolute",
        //        top: "0px",
        //    }
        //});

       
    }
    protected recoverCount: number = 0;
	protected recoverFailCount: number = 0;
    protected readonly maxRecoverFailCount = 5;


    public HistoryStats2(actionIndex: number, gameState: SimpleSlotGameState): ActionStats {
        //let gameState: DoubleUpGameState = JSON.parse(entry2.HistoryGameState.GameState);
		let action: IDoubleUpAction = gameState.Actions[actionIndex] as unknown as IDoubleUpAction;
        let newStat:ActionStats = {
            Action: action.Action,
            Bonus: gameState.GameName,
            Stats: []
        };

        if (action.Action == "DoubleUpStartAction")
        {
            let startAction = action as DoubleUpStartAction;
            newStat.Stats.push(
                {key: "Credit", value: service.moneyFormatter.FormatCurrency(startAction.Credit)},
                //{key: "Credit end", value: service.moneyFormatter.FormatCurrency(startAction.CreditEnd)},
            );        
        }
        else if (action.Action == "DoubleUpPickAction") {
            let pickAction = action as DoubleUpPickAction;
            newStat.Stats.push(
                {key: "Bet", value: service.moneyFormatter.FormatCurrency(pickAction.Bet)},
                {key: "Win", value: service.moneyFormatter.FormatCurrency(pickAction.WinAmounts.TotalWin)},
                {key: "Credit", value: service.moneyFormatter.FormatCurrency(pickAction.Credit)},
                // {key:"Total Bet", value: service.moneyFormatter.FormatCurrency(pickAction.TotalBet)},
                {key: "Collected", value: service.moneyFormatter.FormatCurrency(pickAction.WinSinceGameStart.TotalWin)},
                //{key: "Credit end", value: service.moneyFormatter.FormatCurrency(pickAction.CreditEnd)},
                {key: "Generated Color", value:pickAction.RandomCardColor},               
                {key: "Selected Color", value:pickAction.PlayerSelectedColor}
                
            ); 
        }
        else if (action.Action == "DoubleUpEndAction")
        {
            let endAction = action as DoubleUpEndAction;
            newStat.Stats.push(
                {key: "Credit", value: service.moneyFormatter.FormatCurrency(endAction.Credit)},
                {key: "Collected", value: service.moneyFormatter.FormatCurrency(endAction.WinSinceGameStart.TotalWin)},
                //{key: "Credit end", value: service.moneyFormatter.FormatCurrency(endAction.CreditEnd)},
            ); 

        }else if (action.Action == "DoubleUpCollectHalfAction")
        {
            let collectHalf = action as DoubleUpCollectHalfAction;
            newStat.Stats.push(
                
                {key: "Credit", value: service.moneyFormatter.FormatCurrency(collectHalf.Credit)},
                {key: "Collected", value: service.moneyFormatter.FormatCurrency(collectHalf.WinSinceGameStart.TotalWin)},
                //{key: "Credit end", value: service.moneyFormatter.FormatCurrency(collectHalf.CreditEnd)},
            );

        }else if (action.Action == "DoubleUpCollectAllAction")
        {
            let collectAll = action as DoubleUpCollectAllAction;
            newStat.Stats.push(
                {key: "Credit", value: service.moneyFormatter.FormatCurrency(collectAll.Credit)},
                {key: "Collected", value: service.moneyFormatter.FormatCurrency(collectAll.WinSinceGameStart.TotalWin)},
                //{key: "Credit end", value: service.moneyFormatter.FormatCurrency(collectAll.CreditEnd)},
            );
        }
        return newStat; 

    }
    protected soundFilesNames: string[] = ["DealCard", "PlaceBet","SelectCoin","PlaceCoin"];
    
    public PrepareResources(resourceManager: IWriteOnlyResourceManager) {
        resourceManager.AddGlobal(new DecodedImageResource(this.cardsPath + "redcard.png", ResourceIdentifier.Global.Add("redCard").Scope()));
        resourceManager.AddGlobal(new DecodedImageResource(this.cardsPath + "blackcard.png", ResourceIdentifier.Global.Add("blackCard").Scope()));
        resourceManager.Add(new DecodedImageResource(this.gameResourcesPath + "dialog.png", this.resid.ScopedResourceName(["doubleDialogBackground", "image"])));

        
        this.PrepareCards(resourceManager);
        this.PrepareSounds();
            
    }

    public PrepareCards(resourceManager: IWriteOnlyResourceManager)
    {
        for (let card in cardMap)
        {
            resourceManager.AddGlobal(new DecodedImageResource(this.cardsPath + cardMap[card], ResourceIdentifier.Global.Add(card).Scope()));            
        }
    }

    public PrepareSounds(){
        for(let sound in soundMap){
            this.audioManager.AddTrack(new Track(this.soundsPath+sound+Settings.audioFormat, this.resid.ScopedResourceName(sound)));
        }
        console.log("DoubleUp Sounds: ",this.audioManager);
    }

    public ResourcesLoaded(resourceManager: IReadOnlyResourceManager) {
        this.LoadedTextures(resourceManager);
        

        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.titleDoubleUp.text = this.translator.t.doubleUpTitle));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.historyTitle.text = this.translator.t.doubleUpHistoryTitle));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.lastCardTitle.text = this.translator.t.doubleUpLastCardTitle));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.redCardTxt.text = this.translator.t.doubleUpRedCardTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.blackCardTxt.text = this.translator.t.doubleUpBlackCardTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.collectTxt.text = this.translator.t.doubleUpCollectBtnTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.halfbetTxt.text = this.translator.t.doubleUpRHalfBetBtnTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.winText.text = this.translator.t.doubleUpWinSign));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.loseText.text = this.translator.t.doubleUpLoseSign));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.sign.text = this.translator.t.doubleUpSelectedSign));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.signRed.text = this.translator.t.doubleUpSelectedSign));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.gainText.text = this.translator.t.doubleUpGainTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.signGameLimitTxt.text = this.translator.t.doubleUpGameLimitTxt(this.doubleUpConfiguration.MaxPicks)));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.collectHalfTxt.text = this.translator.t.doubleUpCollectHalfTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.collectAllTxt.text = this.translator.t.doubleUpCollectAllTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.betLimitTxt.text = this.translator.t.doubleUpBetLimitSignTxt));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.winLimitTxt.text = this.translator.t.doubleUpWinLimitSignTxt(service.moneyFormatter.FormatCurrency(this.winLimit))));
        this.translator.SetOnLanguageChangedCallback(() => Safe(() => this.UpdatePaytable()));
    }


    protected lastcard: PIXI.Sprite[] = [];
    protected textureRedCard: PIXI.Texture = null;
    protected textureBlackCard: PIXI.Texture = null;
    protected textureBlackCardFlipped: PIXI.Texture = null;
    protected textureRedCardFlipped: PIXI.Texture = null;
    protected textureDoubleUpBackground: PIXI.Texture = null;

    protected cardTextures: Map<string, PIXI.Texture> = new Map<string, PIXI.Texture>();
    protected cardImageResouces: Map<string, ImageResource> = new Map<string, ImageResource>();
    protected resourceManager: ResourceManager = null;
    protected backSideRed: Texture = null;


    protected LoadedTextures(resourceManager: IReadOnlyResourceManager) {

        let redCardImageResource = resourceManager.Get<DecodedImageResource>(ResourceIdentifier.Global.Add("redCard"));
        let baseTextureRedCardImageResource = new PIXI.BaseTexture(new PIXI.ImageBitmapResource(redCardImageResource.DecodedImage()));
        baseTextureRedCardImageResource.scaleMode = PIXI.SCALE_MODES.NEAREST;
        this.textureRedCard = new PIXI.Texture(baseTextureRedCardImageResource);
        this.backSideRed = this.textureRedCard;
        console.log("TextureRedCard", this.textureRedCard);

        let blackCardImageResource = resourceManager.Get<DecodedImageResource>(ResourceIdentifier.Global.Add("blackCard"));
        let baseTextureblackCardImageResource = new PIXI.BaseTexture(new PIXI.ImageBitmapResource(blackCardImageResource.DecodedImage()));
        baseTextureblackCardImageResource.scaleMode = PIXI.SCALE_MODES.NEAREST;
        this.textureBlackCard = new PIXI.Texture(baseTextureblackCardImageResource);
        console.log("TextureBlackCard", this.textureBlackCard);

        let doubleUpBackgnd = resourceManager.Get<DecodedImageResource>(this.resid.ScopedResourceName(["doubleDialogBackground", "image"]));
        let baseTextureDoubleUpBackground = new PIXI.BaseTexture(new PIXI.ImageBitmapResource(doubleUpBackgnd.DecodedImage()));
        baseTextureDoubleUpBackground.scaleMode = PIXI.SCALE_MODES.NEAREST;
        this.textureDoubleUpBackground = new PIXI.Texture(baseTextureDoubleUpBackground);


        this.CardsLoaded(resourceManager);
    }

    public randomCard(robotSelectedCard: number)
    {
        if (robotSelectedCard == 0)
        {
            void this.pick(CardColor.Black);
        }
        else if (robotSelectedCard == 1)
        {
            void this.pick(CardColor.Red);
        }

    }

    public CardsLoaded(resourceManager: IReadOnlyResourceManager)
    {
        for (let card in cardMap)
        {
            //resourceManager.AddGlobal(new DecodedImageResource(this.blackCardsPath + `${cardMap[card]}.png`, ResourceIdentifier.Global.Add(card).Scope()));
            let cardTextureDecodedValue = resourceManager.Get<DecodedImageResource>(ResourceIdentifier.Global.Add(card));
            let baseCardTextureValue = new PIXI.BaseTexture(new PIXI.ImageBitmapResource(cardTextureDecodedValue.DecodedImage()));
            baseCardTextureValue.scaleMode = PIXI.SCALE_MODES.NEAREST;
            let cardTextureValue = new PIXI.Texture(baseCardTextureValue);
            this.cardTextures.set(card, cardTextureValue);
            this.cardImageResouces.set(card, cardTextureDecodedValue);        
            //"C10" "Cloves_10.png"             
        }
        //console.log("TexturesCards:", this.cardTextures);
        console.log("cardImageResouces:", this.cardTextures);
    }

    protected lastCard: PIXI.Sprite = null;
    protected card: PIXI.Sprite = null;
    protected titleDoubleUp: PIXI.Text = null;
    protected historyTitle: PIXI.Text = null;
    protected lastCardTitle: PIXI.Text = null;
    protected redCardTxt: PIXI.Text = null;
    protected blackCardTxt: PIXI.Text = null;
    protected collectTxt: PIXI.Text = null;
    protected halfbetTxt: PIXI.Text = null;
    protected gainText: PIXI.Text = null;
    protected betAndWinTxt: PIXI.Text = null;
    //protected money: PIXI.Text = null;
    protected roundsCounterContainer: PIXI.Container = null;

    public HistoryView(actionIndex: number, gameState: SimpleSlotGameState):HTMLElement{ 
      
        //let gameState: DoubleUpGameState = JSON.parse(entry2.HistoryGameState.GameState);
        
        console.log("DoubleHistory:",gameState)
		let startAction: IDoubleUpAction = gameState.Actions[actionIndex] as unknown as IDoubleUpAction;
		let triggeredBonus: Bonus = Bonus.None;
        console.log("DoubleHistory:",startAction)

			let doubleGameView: HTMLElement = CreateElement("div", {
				classes: ["doubleUpGameView"],
				style:
				{
					height: Scale.Hpx(975),
					//width: Scale.Wpx(900),
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					flexDirection: "column",
                    
				}
			});

            //background
            let gameView: HTMLElement = CreateElement("div", {
                classes: ["gameBackground"], parentToAppend: doubleGameView,
                style:
                {
                    width: Scale.Wpx(770),
                    height: Scale.Hpx(700),
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: Scale.fhpx(70),
					color: "white",
					textShadow: `${Scale.XY(10)}px ${Scale.XY(10)}px ${Scale.XY(10)}px black, 0px 0px ${Scale.XY(20)}px black, 0px 0px ${Scale.XY(30)}px black`,

                }
            });           

        if (startAction.Action == "DoubleUpStartAction") {
            let historyCard: HTMLElement = CreateElement("div", {
                classes: ["DoubleUpStartAction"], parentToAppend: gameView,
                innerHTML: "Double Up Started"
            });
            
        } else if (startAction.Action == "DoubleUpPickAction") {
            let pickAction = startAction as DoubleUpPickAction

            let selectedColor = pickAction.PlayerSelectedColor.toString();
            let imgCard = this.cardImageResouces.get(pickAction.RandomCard);

            let historyCard: HTMLElement = CreateElement("div", {
                classes: ["historyCard"], parentToAppend: gameView,
                style:
                {
                    width: Scale.Wpx(250),
                    height: Scale.Hpx(360),
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",                   
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundImage: imgCard.CssBackground()
                }
            });
            let winTextHistory: string = null;
            if (pickAction.RandomCardColor == pickAction.PlayerSelectedColor)
            {
                 winTextHistory= "Win";
            }
            else
            {
                winTextHistory="Lose";
            }



            let historyColor: HTMLElement = CreateElement("div", {
                classes: ["historyColor"], parentToAppend: gameView,
                innerHTML: "Selected Color By Player: " + selectedColor,
                style:
                {
                    alignSelf: "baseline",
                    position:"fixed",
                    

                }
            });


            let winOrLose: HTMLElement= CreateElement("div", {
                classes: ["winOrLose"], parentToAppend: gameView,
                innerHTML: winTextHistory,
                style:
                {
                    alignSelf: "end",
                    position:"fixed",

                    

                }
            });
        }
        else if (startAction.Action == "DoubleUpEndAction"){
                let historyCard: HTMLElement = CreateElement("div", {
                    classes: ["DoubleUpStartAction"], parentToAppend: gameView,
                    innerHTML: "Double Up Ended"
                });
        }
        else if (startAction.Action == "DoubleUpCollectHalfAction"){
            let collectHalf: HTMLElement = CreateElement("div", {
                classes: ["DoubleUpCollectHalfAction"], parentToAppend: gameView,
                innerHTML: "Collect Half"
            });
        }
        else if (startAction.Action == "DoubleUpCollectAllAction"){
            let collectAll: HTMLElement = CreateElement("div", {
                classes: ["DoubleUpCollectAllAction"], parentToAppend: gameView,
                innerHTML: "Collect All"
            });
    }



        return doubleGameView;

    }
    protected created: boolean = false;
    protected tryRecoverWebglContext(): boolean
	{
		// Double Up ya no tiene canvas propio (es PIXI en el contenedor del slot); la recuperación WebGL la hace el app principal.
		this.restoreWebGlInterval?.Stop();
		return true;
	}

    protected lostCount: number = 0;
    protected webglcontextlostInstance: () => void = this.webglcontextlost.bind(this);
    protected webglcontextlost()
    {
        this.lostCount++;
        Log.Error(this.logGroup, `Webgl context lost, count: ${this.lostCount}`);

	    void service.api?.frontendError(FrontendErrorCode.WebGLContextLost, `${this.resid.ID()}, count: ${this.lostCount}`);

		// try recover right now, or start timer to try recover later
		let success = this.tryRecoverWebglContext();
		if (success)
		{
			// all good
			return;
		}
        
		
		// error recovering, start timer
		this.restoreWebGlInterval.Start();
    }

	/** Sube la resolución de todos los PIXI.Text del contenedor para que se vean más nítidos. */
	private setTextsCrispRecursive(obj: PIXI.DisplayObject): void {
		if (obj instanceof PIXI.Text) {
			const dpr = (typeof window !== "undefined" && (window as any).devicePixelRatio) ? (window as any).devicePixelRatio : 1;
			// Forzamos al menos x2 para evitar texto borroso cuando el canvas se escala.
			obj.resolution = Math.max(2, dpr);
		}

		if ((obj as any).children && Array.isArray((obj as any).children)) {
			for (const child of (obj as any as PIXI.Container).children) {
				this.setTextsCrispRecursive(child);
			}
		}
	}

    private createPIXIDoubleUp()
    {
        if (!this.textureDoubleUpBackground) {
            throw new Error("DoubleUp: textures not loaded yet, ensure PrepareResources/ResourcesLoaded run before opening DoubleUp");
        }
        this.isSmallView = typeof window !== "undefined" && window.innerWidth < 768;
        if (this.isSmallView) {
            const w = Math.min(Scale.W(300), Scale.H(300));
            const h = Math.min(Scale.W(155), Scale.H(155));
            this.gameButtonsSize = new Size(w, h);
            this.controlButtonsSize = new Size(w, h);
        }
        this.doubleUpContainer = new MyPIXIContainer(this.resid.ScopedResourceName("DoubleUpContainer"));
        this.doubleUpContainer.interactive = true;
        this.doubleUpContainer.eventMode = "static";
        this.doubleUpContainer.visible = false;

        let doubleUpBckgnd = PIXI.Sprite.from(this.textureDoubleUpBackground);
        doubleUpBckgnd.width = this.viewSize.Width;
        doubleUpBckgnd.height = Scale.H(this.HEIGHT);
        doubleUpBckgnd.x = 0;
        doubleUpBckgnd.y = 0;

        let textstyle = new PIXI.TextStyle({
            fontFamily: 'NotoSansDisplay-Black',
            fontSize: Scale.fh(50),
            fill: 'Yellow',
            stroke: 'black',
            strokeThickness: Scale.fh(6),
            lineJoin : "round"
        });
        this.titleDoubleUp = new PIXI.Text(this.translator.t.doubleUpTitle, textstyle);
        this.titleDoubleUp.name = this.resid.ScopedResourceName("titleDoubleUp");
        const dw = this.designWidth();
        const dh = this.designHeight();
        this.titleDoubleUp.x = dw / 2 - this.titleDoubleUp.width / 2;
        this.titleDoubleUp.y = dh / 10 - this.titleDoubleUp.height / 2;

        this.doubleUpContainer.addChild(doubleUpBckgnd, this.titleDoubleUp);

        this.createHistory();
        this.prepareSelectedRedButtonSign();
        this.prepareSelectedBlackButtonSign();
        this.prepareGameLimitSign();
        this.prepareWinLimitContainer();
        this.prepareRoundsCounter();
        this.createGameView();
        this.createControls();
        this.prepareSelectedCardContainer();
        this.prepareLoseLayout();
        this.prepareWinLayout();
        this.prepareCollectAllLayoutTxt();
        this.prepareCollectHalfLayoutTxt();
        this.prepareBetLimitSignTxt();

        this.historyTitle.text = this.translator.t.doubleUpHistoryTitle;
        this.lastCardTitle.text = this.translator.t.doubleUpLastCardTitle;
        this.redCardTxt.text = this.translator.t.doubleUpRedCardTxt;
        this.blackCardTxt.text = this.translator.t.doubleUpBlackCardTxt;
        this.collectTxt.text = this.translator.t.doubleUpCollectBtnTxt;
        this.halfbetTxt.text = this.translator.t.doubleUpRHalfBetBtnTxt;
        this.gainText.text = this.translator.t.doubleUpGainTxt;
        this.signGameLimitTxt.text = this.translator.t.doubleUpGameLimitTxt(this.doubleUpConfiguration.MaxPicks);

		// Ajuste final: subir resolución de todos los textos para que no se vean borrosos al escalar.
		this.setTextsCrispRecursive(this.doubleUpContainer);
    }

    protected readonly designWidth = (): number => this.viewSize?.Width ?? 1920;
    protected readonly designHeight = (): number => Scale.H(this.HEIGHT);

    /** Contenedor raíz para añadir hijos (equivalente a app.stage cuando era PIXI propio). */
    protected get stage(): PIXI.Container {
        return this.doubleUpContainer;
    }

    public Create(): void
    {
        if (this.created) return;
        try {
            this.CreatePIXIAPP();
            this.created = true;
        } catch (ex) {
            this.created = false;
            console.error("DoubleUp CreatePIXIAPP failed", ex);
            this.restoreWebGlInterval.Start();
        }
    }

    /** Crea el contenedor PIXI y su contenido (como Buy Bonus). El slot añade el resultado a GetReelsGameResponsiveContainer(). */
    public CreatePIXIAPP(): void {
        this.createPIXIDoubleUp();
    }

    /** Devuelve el contenedor PIXI para que el slot lo añada al reels container (como Buy Bonus). */
    public ViewPIXIAPP(): PIXI.Container {
        return this.doubleUpContainer;
    }

    public Destroy(): void
    {
        this.internalDestroy();
        this.created = false;
        this.restoreWebGlInterval.Stop();
        this.doubleUpContainer = null;
    }

    public internalDestroy()
    {
        if (!this.created) {
            Log.Error(this.logGroup, `Destroy(): not created, not destroying anything`);
            return;
        }
        Safe(() => this.doubleUpContainer?.destroy({ children: true }));
        this.doubleUpContainer = null;
    }
    

    protected historyCardsCont: PIXI.Container = null;
    protected containerHistory: PIXI.Container = null;
    protected signGameLimitTxt: PIXI.Text = null;
    protected signGameLimitContainer: PIXI.Container = null;
    protected playedGames: PIXI.Text = null;
    protected roundsCounterTxtContainer: PIXI.Container = null;
    protected roundNumber: PIXI.Text = null;

    private limitGameHeadertxt: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: 'NotoSansDisplay-Black',
        fontSize: Scale.fh(35),
        fill: 'Yellow',
        stroke: 'black',
        strokeThickness: Scale.fh(6),
        lineJoin: "round"
    });

    protected prepareGameLimitSign() {
        this.signGameLimitTxt = new PIXI.Text("", this.limitGameHeadertxt);
        this.signGameLimitTxt.name = this.resid.ScopedResourceName("signGameLimitTxt");

        this.signGameLimitContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.signGameLimitContainer.width = this.gameContainerSize.Width / 4;
        this.signGameLimitContainer.height = this.gameContainerSize.Height / 8;
        this.signGameLimitContainer.x = (this.gameContainerSize.Width / 4) * 3 - (this.gameContainerSize.Width / 4) / 2;
        this.signGameLimitContainer.y = this.gameContainerSize.Height / 12;

        let signGameLimitTxtCont = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        signGameLimitTxtCont.width = this.gameContainerSize.Width / 4;
        signGameLimitTxtCont.height = ((this.gameContainerSize.Height / 16));
        signGameLimitTxtCont.x = 0;
        signGameLimitTxtCont.y = 0;
        this.signGameLimitTxt.anchor.set(0.5, 0.5);
        this.signGameLimitTxt.position.set((this.gameContainerSize.Width / 8), (this.gameContainerSize.Height / 16));
        signGameLimitTxtCont.addChild(this.signGameLimitTxt);

        this.signGameLimitContainer.addChild(signGameLimitTxtCont)

        this.stage.addChild(this.signGameLimitContainer);
        this.signGameLimitContainer.visible = false;
        this.showLimitGameSign();
    }

    protected gameContainerSize: Size = new Size(Scale.W(1920), Scale.H(this.HEIGHT));

    protected winLimitCont: PIXI.Container = null;
    protected winLimitTxtCont: PIXI.Container = null;
    protected winLimitTxt: PIXI.Text = null;

    protected prepareWinLimitContainer()
    {
        this.winLimitCont = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.winLimitCont.width = this.gameContainerSize.Width / 4;
        this.winLimitCont.height = this.gameContainerSize.Height / 8;
        this.winLimitCont.x = (this.gameContainerSize.Width / 4); /*- (this.gameContainerSize.width / 4) / 2;*/
        this.winLimitCont.y = this.gameContainerSize.Height / 12;

        this.winLimitTxtCont = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.winLimitTxtCont.width = this.gameContainerSize.Width / 4;
        this.winLimitTxtCont.height = this.gameContainerSize.Height / 8;
        this.winLimitTxtCont.x = (this.gameContainerSize.Width / 32);
        this.winLimitTxtCont.y = this.gameContainerSize.Height / 12;
        
        
        
        this.prepareWinLimitText();


    }

    protected prepareWinLimitText()
    {
        this.winLimitTxt = new PIXI.Text(
            this.translator.t.doubleUpWinLimitSignTxt(service.moneyFormatter.FormatCurrency(this.backendSettings?.GetNum("play.doubleuplimit", 100) ?? 100)),
            this.betLimitStyle
        );
        this.winLimitTxt.name = this.resid.ScopedResourceName("winLimitTxt");
        this.winLimitTxt.anchor.set(0.5, 0);
        this.winLimitTxt.position.set((0), (this.gameContainerSize.Height / 32));
        this.winLimitTxtCont.addChild(this.winLimitTxt);
        this.winLimitCont.addChild(this.winLimitTxtCont);

        this.stage.addChild(this.winLimitCont);

    }
    protected prepareRoundsCounter() {
        this.roundsCounterContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.roundsCounterContainer.width = this.gameContainerSize.Width / 4;
        this.roundsCounterContainer.height = this.gameContainerSize.Height / 8;
        this.roundsCounterContainer.x = (this.gameContainerSize.Width / 4) * 3 - (this.gameContainerSize.Width / 4) / 2;
        this.roundsCounterContainer.y = this.gameContainerSize.Height / 12;

        this.roundsCounterTxtContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.roundsCounterTxtContainer.width = this.gameContainerSize.Width / 4;
        this.roundsCounterTxtContainer.height = ((this.gameContainerSize.Height / 32));
        this.roundsCounterTxtContainer.x = 0;
        this.roundsCounterTxtContainer.y = ((this.gameContainerSize.Height / 8) / 2);
        
        this.stage.addChild(this.roundsCounterContainer);
        this.roundsCounterContainer.visible = false
        this.prepareTextRound();

    }
    protected prepareTextRound() {
        this.roundNumber = new PIXI.Text(0 + "/" + this.doubleUpConfiguration.MaxPicks, this.limitGameHeadertxt);
        this.roundNumber.name = this.resid.ScopedResourceName("roundNumber");
        this.roundNumber.anchor.set(0.5, 0);
        this.roundNumber.position.set((this.gameContainerSize.Width / 8), (this.gameContainerSize.Height / 32));
        this.roundsCounterTxtContainer.addChild(this.roundNumber);
        this.roundsCounterContainer.addChild(this.roundsCounterTxtContainer)
        this.roundsCounterContainer.visible = true;
    }

    protected showRounds(nRounds: number) {
        if (this.roundNumber) {
            this.roundNumber.text = ((nRounds) + "/" + this.doubleUpConfiguration.MaxPicks);
        }
    }

    protected showLimitGameSign() {

        this.signGameLimitContainer.visible = true;

    }

    private headersTxt: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: 'NotoSansDisplay-Black',
        fontSize: Scale.fh(35),
        fill: 'White',
        stroke: 'black',
        strokeThickness: Scale.fh(6),
        lineJoin: "round"
    });
    
    protected createHistory() {

        this.containerHistory = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.containerHistory.width = this.gameContainerSize.Width;
        this.containerHistory.height = this.gameContainerSize.Height / 4;
        this.containerHistory.x = 0;
        this.containerHistory.y = this.gameContainerSize.Height / 6;

        this.historyCardsCont = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.historyCardsCont.width = this.gameContainerSize.Width / 2;
        this.historyCardsCont.height = this.containerHistory.height / 4;
        this.historyCardsCont.y = 0;
        this.historyCardsCont.x = this.gameContainerSize.Width / 2;

        this.historyCardsContTitle = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.historyCardsContTitle.width = this.gameContainerSize.Width / 2;
        this.historyCardsContTitle.height = this.containerHistory.height / 4;
        this.historyCardsContTitle.y = 0;
        this.historyCardsContTitle.x = this.gameContainerSize.Width / 2;

        this.historyTitle = new PIXI.Text("", this.headersTxt);
        this.historyTitle.name = this.resid.ScopedResourceName("historyTitle");
        this.historyTitle.style.wordWrap = true;
        this.historyTitle.style.wordWrapWidth = 1000;
        this.historyTitle.x = (this.historyCardsCont.width / 2);

        this.historyCardsContTitle.addChild(this.historyTitle);
        this.lastCardTitle = new PIXI.Text("", this.headersTxt);
        this.lastCardTitle.name = this.resid.ScopedResourceName("lastCardTitle");

        this.lastCardContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.lastCardContainer.width = this.gameContainerSize.Width / 2;
        this.lastCardContainer.height = this.gameContainerSize.Height / 4;
        this.lastCardContainer.position.set(this.gameContainerSize.Width / 4 - this.gameButtonsSize.Width / 2, -this.gameContainerSize.Height / 12)


        this.lastCardContainerTitle = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.lastCardContainerTitle.width = this.gameContainerSize.Width / 2;
        this.lastCardContainerTitle.height = this.gameContainerSize.Height / 4;
        this.lastCardContainerTitle.position.set(this.gameContainerSize.Width / 4 - this.gameButtonsSize.Width / 2, -this.gameContainerSize.Height / 12)

        if (this.showBigLastCard)
        {
            this.lastCardTitle.x = (this.lastCardContainerTitle.width / 2);

            this.lastCardContainerTitle.addChild(this.lastCardTitle);
        }


        this.containerHistory.addChild(this.lastCardContainerTitle, this.historyCardsContTitle, this.historyCardsCont, this.lastCardContainer);
        this.stage.addChild(this.containerHistory);
        //Create last card image
        this.showLastCard();

     
    }
    protected showLastCard()
    {

        this.lastCard = new PIXI.Sprite();       
        let lastcardsize: Size = new Size(Scale.W(150), Scale.H(200));
        this.lastCard.position.set(this.lastCardContainer.width/2, lastcardsize.Width);
        this.lastCard.width = lastcardsize.Width;
        this.lastCard.height = lastcardsize.Height;
        this.lastCard.anchor.set(Scale.X(-0.5),Scale.Y(0.5));
        
        if (!this.showBigLastCard)
        {
            return

        }
        this.lastCardContainer.addChild(this.lastCard)  
    }

    public OnButtonChanged(button: Button, pressed: boolean)
    {
        if (pressed)
        {
            if (button == Button.ButtonMaxBet)
            {
                void this.pick(CardColor.Black);
            }
            else if (button == Button.ButtonAutoplay)
            {
                void this.pick(CardColor.Red);
            }
            else if (button == Button.ButtonSpin)
            {
                this.pickCollectAll();
            }
        }


    }

    protected textureCard: PIXI.Texture<PIXI.Resource>[] = [];
    protected halfBetBtn:PIXI.Container = null;
    protected collectBetBtn:PIXI.Container = null;

    public createControls() {
        // let gainContSize: Size = new Size(Scale.W(500), Scale.H(75));

        const controlFontSize = this.isSmallView ? 32 : 40;
        let btntxt = new PIXI.TextStyle({
            fontFamily: 'NotoSansDisplay-Black',
            fontSize: Scale.fh(controlFontSize),
            fill: 'white',
            stroke: 'black',
            strokeThickness: Scale.fh(this.isSmallView ? 5 : 6),
            lineJoin: "round",
            wordWrap: true,
            wordWrapWidth: this.controlButtonsSize.Width - Scale.W(16),
            align: "center",
        });

        let moneyText = new PIXI.TextStyle({
            fontFamily: 'NotoSansDisplay-Black',
            fontSize: Scale.fh(50),
            fill: 'white',
            stroke: 'black',
            strokeThickness: Scale.fh(6),
            lineJoin: "round"

        });

        this.collectTxt = new PIXI.Text(this.translator.t.doubleUpCollectBtnTxt, btntxt);
        this.collectTxt.name = this.resid.ScopedResourceName("collectTxt");
        this.halfbetTxt = new PIXI.Text("", btntxt);
        this.halfbetTxt.name = this.resid.ScopedResourceName("halfbetTxt");
        this.gainText = new PIXI.Text("", btntxt);
        this.gainText.name = this.resid.ScopedResourceName("gainText");
        //this.money = new PIXI.Text("$0.00", moneyText);

        this.controlsContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.controlsContainer.width = this.gameContainerSize.Width;
        this.controlsContainer.height = this.gameContainerSize.Height;
        this.controlsContainer.x = 0;
        this.controlsContainer.y = (this.gameContainerSize.Height / 14);

        this.collectBetBtn = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.collectBetBtn.width = this.controlButtonsSize.Width;
        this.collectBetBtn.height = this.controlButtonsSize.Height;
        this.collectBetBtn.position.set(0, this.controlButtonsSize.Height / 2);

        let collectBtnBackground = this.createCollectbtnBackgndGradient();

        let collectbtntxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        collectbtntxt.width = this.controlButtonsSize.Width;
        collectbtntxt.height = this.controlButtonsSize.Height;
        this.collectTxt.anchor.set(0.5, 0.5);
        this.collectTxt.position.set(this.controlButtonsSize.Width / 2, this.controlButtonsSize.Height / 2);
        collectbtntxt.addChild(this.collectTxt);

        let collectbtnrectangle = new PIXI.Graphics();
        collectbtnrectangle
            .lineStyle(4, 0xFFEA00, 4)
            .drawRect(this.gameContainerSize.Width / 4 - this.controlButtonsSize.Width / 2, (this.gameContainerSize.Height / 8) * 5.75 - (this.controlButtonsSize.Height / 2), this.controlButtonsSize.Width, this.controlButtonsSize.Height);

        
        this.collectBetBtn.addChild(collectBtnBackground, collectbtntxt, collectbtnrectangle);
        this.collectBetBtn.interactive = true;
        //this.showGainBackgndGradient();
        //let gainContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        //gainContainer.width = gainContSize.width;
        //gainContainer.height = gainContSize.height;
//
        //let lastWinTxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        //lastWinTxt.width = gainContSize.width;
        //lastWinTxt.height = gainContSize.height / 2
        //lastWinTxt.x = Scale.X(0);
        //lastWinTxt.y = Scale.Y(0);

        //this.gainText.anchor.set(0.5, 0.5);
        //this.gainText.position.set(gainContSize.width / 2, ((gainContSize.height / 3) / 2)-((gainContSize.height)/2));
        //lastWinTxt.addChild(this.gainText);

        // let wonMoney = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        // wonMoney.width = gainContSize.width;
        // wonMoney.height = gainContSize.height / 2;
        // wonMoney.x = Scale.X(0);
        // wonMoney.y = Scale.Y(0)

        // this.money.anchor.set(0.5, 0.5);
        // this.money.position.set(gainContSize.width / 2 - wonMoney.width / 2,  gainContSize.height / 2- wonMoney.height/2);
        // wonMoney.addChild(this.money);

        //gainContainer.addChild(lastWinTxt, wonMoney);

        // let gainContBackgnd = new PIXI.Graphics();
        // gainContBackgnd
        //     .lineStyle(4, 0xFFEA00, 4)
        //     .drawRect(this.gameContainerSize.width / 2 - gainContSize.width / 2, (this.gameContainerSize.height / 8) * 6 - (gainContSize.height / 2), gainContSize.width, gainContSize.height);


        let collectHalfBackground = this.createCollectHalfGradient();

        this.halfBetBtn = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.halfBetBtn.width = this.controlButtonsSize.Width;
        this.halfBetBtn.height = this.controlButtonsSize.Height;
        this.halfBetBtn.position.set(0, this.controlButtonsSize.Height / 2);

        let halfbetbtntxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        halfbetbtntxt.width = this.controlButtonsSize.Width;
        halfbetbtntxt.height = this.controlButtonsSize.Height;
        this.halfbetTxt.anchor.set(0.5, 0.5);
        this.halfbetTxt.position.set(this.controlButtonsSize.Width / 2, this.controlButtonsSize.Height / 2);
        halfbetbtntxt.addChild(this.halfbetTxt);

        let halfbetbtnrectangle = new PIXI.Graphics();
        halfbetbtnrectangle
            .lineStyle(4, 0xFFEA00, 4)
            .drawRect((this.gameContainerSize.Width * 0.75) - (this.controlButtonsSize.Width / 2), (this.gameContainerSize.Height / 8) * 5.75 - (this.controlButtonsSize.Height / 2), this.controlButtonsSize.Width, this.controlButtonsSize.Height);

        //gainContainer.x = (this.gameContainerSize.width / 2) - (gainContSize.width / 2);
        //gainContainer.y = (this.gameContainerSize.height / 8) * 6 - (gainContSize.height / 2);
        collectbtntxt.x = (this.gameContainerSize.Width / 4) - (this.controlButtonsSize.Width / 2);
        collectbtntxt.y = (this.gameContainerSize.Height / 8) * 5.75 - (this.controlButtonsSize.Height / 2);
        halfbetbtntxt.x = (this.gameContainerSize.Width * 0.75) - (this.controlButtonsSize.Width / 2);
        halfbetbtntxt.y = (this.gameContainerSize.Height / 8) * 5.75 - (this.controlButtonsSize.Height / 2);


        this.halfBetBtn.addChild(collectHalfBackground, halfbetbtntxt, halfbetbtnrectangle);
        this.halfBetBtn.interactive = true;

        this.halfBetBtn.addListener("pointerdown", (ev: MouseEvent) => {
                this.pickCollectHalf();
        });

        this.collectBetBtn.addListener("pointerdown", (ev: MouseEvent) => {
            this.pickCollectAll();
        });


        this.controlsContainer.addChild(this.collectBetBtn, this.halfBetBtn/*gainContainer, gainContBackgnd*/)
        this.stage.addChild(this.controlsContainer);
    }

    protected cardSize: Size = new Size(Scale.W(290), Scale.H(400));

    protected blackbtn: PIXI.Container = null;
    protected redbtn: PIXI.Container = null;

    public createGameView() {

        const gameBtnFontSize = this.isSmallView ? 50 : 56;
        let btntxt = new PIXI.TextStyle({
            fontFamily: 'NotoSansDisplay-Black',
            fontSize: Scale.fh(gameBtnFontSize),
            fill: 'white',
            stroke: 'black',
            strokeThickness: Scale.fh(this.isSmallView ? 7 : 8),
            lineJoin: "round"

        });

        this.redCardTxt = new PIXI.Text("", btntxt);
        this.redCardTxt.name = this.resid.ScopedResourceName("redCardTxt");
        this.blackCardTxt = new PIXI.Text("", btntxt);
        this.blackCardTxt.name = this.resid.ScopedResourceName("blackCardTxt");
        this.betAndWinTxt = new PIXI.Text("", this.headersTxt);
        this.betAndWinTxt.name = this.resid.ScopedResourceName("betAndWinTxt");


        this.gameViewContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.gameViewContainer.width = this.gameContainerSize.Width;
        this.gameViewContainer.height = this.gameContainerSize.Height / 4;
        this.gameViewContainer.x = 0;
        this.gameViewContainer.y = (this.gameContainerSize.Height / 22);

        this.redbtn = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.redbtn.width = this.gameButtonsSize.Width / 2;
        this.redbtn.height = this.gameButtonsSize.Height / 2;
        this.redCardTxt.anchor.set(0.5, 0.5);
        this.redCardTxt.position.set(this.gameButtonsSize.Width / 2, this.gameButtonsSize.Height / 2 + this.cardSize.Width/2);

        
        let rectangleredbtntxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        rectangleredbtntxt.width = this.gameButtonsSize.Width / 2;
        rectangleredbtntxt.height = this.gameButtonsSize.Height / 2;
        this.redCardTxt.anchor.set(0.5, 0.5);
        this.redCardTxt.position.set(this.gameButtonsSize.Width / 2, this.gameButtonsSize.Height / 2 + this.cardSize.Width/2);
        rectangleredbtntxt.addChild(this.redCardTxt);

        let rectangleredbtn = new PIXI.Graphics();
        rectangleredbtn
            .lineStyle(4, 0xFFEA00, 4)
            .drawRect(this.gameContainerSize.Width / 4 - this.gameButtonsSize.Width / 2, this.gameContainerSize.Height / 2.2 - this.gameButtonsSize.Height / 2 + this.cardSize.Width/2, this.gameButtonsSize.Width, this.gameButtonsSize.Height);

        this.betAndWinContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.betAndWinContainer.width = this.controlButtonsSize.Width;
        this.betAndWinContainer.height = this.controlButtonsSize.Height;
        this.betAndWinTxt.anchor.set(0.5, 0.5);
        this.betAndWinTxt.position.set(this.controlButtonsSize.Width / 2, this.controlButtonsSize.Height / 2);
        this.betAndWinContainer.addChild(this.betAndWinTxt);
        this.betAndWinContainer.visible=true;

        this.blackbtn = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.blackbtn.width = this.gameButtonsSize.Width;
        this.blackbtn.height = this.gameButtonsSize.Height;
        this.blackCardTxt.anchor.set(0.5, 0.5);
        this.blackCardTxt.position.set(this.gameButtonsSize.Width / 2, this.gameButtonsSize.Height / 2 + this.cardSize.Width/2);

        let rectangleblackbtntxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        rectangleblackbtntxt.width = this.gameButtonsSize.Width;
        rectangleblackbtntxt.height = this.gameButtonsSize.Height;
        this.blackCardTxt.anchor.set(0.5, 0.5);
        this.blackCardTxt.position.set(this.gameButtonsSize.Width / 2, this.gameButtonsSize.Height / 2 + this.cardSize.Width/2);
        rectangleblackbtntxt.addChild(this.blackCardTxt);
        
        let rectangleblackbtn = new PIXI.Graphics();
        rectangleblackbtn
        .lineStyle(4, 0xFFEA00, 4)
        .drawRect((this.gameContainerSize.Width * 0.75) - this.gameButtonsSize.Width / 2, this.gameContainerSize.Height / 2.2 - this.gameButtonsSize.Height / 2 + this.cardSize.Width/2, this.gameButtonsSize.Width, this.gameButtonsSize.Height);
        

        this.card = new PIXI.Sprite(this.textureBlackCard);
        this.card.width = this.cardSize.Width;
        this.card.height = this.cardSize.Height;
        this.textureCard.push(this.textureBlackCard);
        this.textureCard.push(this.textureRedCard);
               
        rectangleredbtntxt.x = (this.gameContainerSize.Width / 4) - (this.gameButtonsSize.Width / 2);
        rectangleredbtntxt.y = (this.gameContainerSize.Height / 2.2) - (this.gameButtonsSize.Height / 2);
        rectangleblackbtntxt.x = (this.gameContainerSize.Width * 0.75) - (this.gameButtonsSize.Width / 2);
        rectangleblackbtntxt.y = (this.gameContainerSize.Height / 2.2) - (this.gameButtonsSize.Height / 2);
        // this.betAndWinContainer.x = (this.gameContainerSize.width *0.75) - (this.gameButtonsSize.width / 2);
        // this.betAndWinContainer.y = (this.gameContainerSize.height / 2) - (this.gameButtonsSize.height / 2);
        
        let blackBackground = this.createBlackGradientBtn();
        let redBackground = this.createRedGradientBtn();
        //rectangleblackbtn.addChild(this.blackGradContainer, rectangleblackbtntxt)
        this.blackbtn.interactive = true;
        this.blackbtn.addChild(blackBackground, rectangleblackbtntxt, rectangleblackbtn);

        this.redbtn.interactive = true;
        this.redbtn.addChild(redBackground, rectangleredbtntxt, rectangleredbtn);


        // Texto "PICK COLOR AND BET" más abajo para no encimar con las cartas pequeñas de arriba.
        this.betAndWinContainer.position.set(this.gameContainerSize.Width / 2 - (this.controlButtonsSize.Width / 2), this.gameContainerSize.Height / 3.2);

        this.card.x = (this.gameContainerSize.Width / 2) - (this.cardSize.Width / 2);
        this.card.y = (this.gameContainerSize.Height * 0.65) - (this.cardSize.Height / 2);

        this.gameViewContainer.addChild(this.redbtn, this.blackbtn, this.card, this.betAndWinContainer)
        this.stage.addChild(this.gameViewContainer);

        this.blackbtn.addListener("pointerdown", (ev: MouseEvent) => {
            void this.pickBlackCard();
        });

        
        this.redbtn.addListener("pointerdown", (ev: MouseEvent) => {
            void this.pickRedCard();
        });
    }


    //protected duration: number = 1;
    protected creatingAnimation(pickSequence: number) {

        let percentComplete = pickSequence / (this.doubleUpConfiguration.MaxPicks -1);

        let slowest = 1;
        let fastest = 0.1;

        let time = slowest - ((slowest - fastest) * percentComplete)

        this.anim = TweenLite.to(this.card, {
            duration: time, repeat: -1, yoyo: true, paused: true, onRepeat: () => {
                if (this.index == 0) {
                    this.index = 1;
                } else {
                    this.index = 0;
                }
                if (this.card && this.textureCard?.[this.index]) {
                    this.card.texture = this.textureCard[this.index];
                }
            }
        });
    }


    private textstyle: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: 'NotoSansDisplay-Black',
        fontSize: Scale.fh(40),
        fill: 'Yellow',
        stroke: 'black',
        strokeThickness: Scale.fh(6),
        lineJoin: "round"
    });

    private betLimitStyle: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: 'NotoSansDisplay-Black',
        fontSize: Scale.fh(45),
        fill: 'Red',
        stroke: 'black',
        strokeThickness: Scale.fh(6),
        lineJoin: "round"
    });
    protected index: number = 0;

    protected prepareWinLayout() {

        this.winLayoutContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.winLayoutContainer.width = this.gameContainerSize.Width;
        this.winLayoutContainer.height = this.gameContainerSize.Height / 4;
        this.winLayoutContainer.x = 0;
        this.winLayoutContainer.y = 0;
        this.winText = new PIXI.Text(this.translator.t.doubleUpWinSign, this.textstyle);
        this.winText.name = this.resid.ScopedResourceName("winText");
        this.winText.anchor.set(0.5, 0.5);
        //this.winText.position.set(this.gameContainerSize.width / 2, (this.gameContainerSize.height / 8) * 3);
        this.winText.position.set(this.gameContainerSize.Width / 2, (this.gameContainerSize.Height / 2)+this.cardSize.Width/2);
        this.winLayoutContainer.addChild(this.winText);
        this.stage.addChild(this.winLayoutContainer);
        this.winLayoutContainer.visible = false;
    }

    protected prepareCollectHalfLayoutTxt()
    {
        this.collecHalfContainerTxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.collecHalfContainerTxt.width = this.gameContainerSize.Width;
        this.collecHalfContainerTxt.height = this.gameContainerSize.Height / 4;
        this.collecHalfContainerTxt.x = 0;
        this.collecHalfContainerTxt.y = 0;
        this.collectHalfTxt = new PIXI.Text(this.translator.t.doubleUpCollectHalfTxt, this.textstyle);
        this.collectHalfTxt.name = this.resid.ScopedResourceName("collectHalfTxt");
        this.collectHalfTxt.anchor.set(0.5, 0.5);
        this.collectHalfTxt.position.set(this.gameContainerSize.Width / 2, (this.gameContainerSize.Height / 2)+this.cardSize.Width/2);
        this.collecHalfContainerTxt.addChild(this.collectHalfTxt);
        this.stage.addChild(this.collecHalfContainerTxt);
        this.collecHalfContainerTxt.visible = false;
        
    }

    protected prepareCollectAllLayoutTxt()
    {
        this.collectAllContainerTxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.collectAllContainerTxt.width = this.gameContainerSize.Width;
        this.collectAllContainerTxt.height = this.gameContainerSize.Height / 4;
        this.collectAllContainerTxt.x = 0;
        this.collectAllContainerTxt.y = 0;
        this.collectAllTxt = new PIXI.Text(this.translator.t.doubleUpCollectAllTxt, this.textstyle);
        this.collectAllTxt.name = this.resid.ScopedResourceName("collectAllTxt");
        this.collectAllTxt.anchor.set(0.5, 0.5);
        this.collectAllTxt.position.set(this.gameContainerSize.Width / 2, (this.gameContainerSize.Height /2)+this.cardSize.Width/2);
        this.collectAllContainerTxt.addChild(this.collectAllTxt);
        this.stage.addChild(this.collectAllContainerTxt);
        this.collectAllContainerTxt.visible = false;
        
    }
    protected prepareBetLimitSignTxt()
    {
        this.betLimitContainerTxt = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.betLimitContainerTxt.width = this.gameContainerSize.Width;
        this.betLimitContainerTxt.height = this.gameContainerSize.Height / 4;
        this.betLimitContainerTxt.x = 0;
        this.betLimitContainerTxt.y = 0;
        this.betLimitTxt = new PIXI.Text(this.translator.t.doubleUpBetLimitSignTxt, this.betLimitStyle);
        this.betLimitTxt.name = this.resid.ScopedResourceName("betLimitTxt");
        this.betLimitTxt.anchor.set(0.5, 0.5);
        this.betLimitTxt.position.set(this.gameContainerSize.Width / 2, (this.gameContainerSize.Height /2)+this.cardSize.Width/2);
        this.betLimitContainerTxt.addChild(this.betLimitTxt);
        this.stage.addChild(this.betLimitContainerTxt);
        this.betLimitContainerTxt.visible = false;

    }
    protected showBetLimitSign()
    {
        this.betLimitContainerTxt.visible = true;
    }

    protected prepareLoseLayout() {
        this.loseLayoutContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.loseLayoutContainer.width = this.gameContainerSize.Width;
        this.loseLayoutContainer.height = this.gameContainerSize.Height / 4;
        this.loseLayoutContainer.x = 0;
        this.loseLayoutContainer.y = 0;
        this.loseLayoutContainer.zIndex = 150;
        this.loseText = new PIXI.Text(this.translator.t.doubleUpLoseSign, this.textstyle);
        this.loseText.name = this.resid.ScopedResourceName("loseText");
        this.loseText.anchor.set(0.5, 0.5);
        this.loseText.position.set(this.gameContainerSize.Width / 2, (this.gameContainerSize.Height / 2)+this.cardSize.Width/2);
        this.loseLayoutContainer.addChild(this.loseText);
        this.stage.addChild(this.loseLayoutContainer);
        this.loseLayoutContainer.visible = false;
    }

    protected indexDataDoubleUp: number = 0;
    protected cardSizeHistory: Size = new Size(Scale.W(120), Scale.H(150));

    protected history: Card[] = [];
    

    protected displayedHistory: DisplayedCard[] = [];
    readonly CARDS_IN_HISTORY: number = 5;

    protected showHistory(history: Card[])
    {
        this.history = history;

        this.displayedHistory = [...history]
            .reverse()
            .fill(null, Math.min(history.length, this.CARDS_IN_HISTORY), this.CARDS_IN_HISTORY)
            .slice(0, this.CARDS_IN_HISTORY);

        this.showHistoryInternal();
    }

    // protected showHistoryWithRevealedCard(history: Card[])
    // {
    //     this.history = history;

    //     this.displayedHistory = [...history]
    //         .reverse()
    //         .fill(null, Math.min(history.length, this.CARDS_IN_HISTORY), this.CARDS_IN_HISTORY)
    //         .slice(0, this.CARDS_IN_HISTORY);

    //     this.showHistoryInternal();
    // }
    

    protected showHistoryInternal() {

        if (!this.historyCardsCont) return;

        this.historyCardsCont.removeChildren();

        for (let i = 0; i < this.CARDS_IN_HISTORY; i++)
        {
            let cardHistory = this.displayedHistory[i];

            let cardSprite: PIXI.Sprite = null;
            if (!cardHistory)
            {
                cardSprite = new PIXI.Sprite(this.backSideRed);
            }
            else
            {
                cardSprite = new PIXI.Sprite(this.cardTextures.get(cardHistory));
            }
                
                cardSprite.position.set(Scale.X(130)*i,this.cardSizeHistory.Width);
                cardSprite.width = this.cardSizeHistory.Width;
                cardSprite.height = this.cardSizeHistory.Height;
                cardSprite.anchor.set(Scale.X(0.5),Scale.Y(0.5));
                this.historyCardsCont.addChild(cardSprite);
        }
        
    }
    protected sign: PIXI.Text = null;
    protected signRed: PIXI.Text = null;
    protected signSize: Size = new Size(Scale.W(150), Scale.H(75));
    protected signTxt: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: 'NotoSansDisplay-Black',
        fontSize: Scale.fh(30),
        fill: 'Yellow',
        stroke: 'black',
        strokeThickness: Scale.fh(6),
        lineJoin: "round"

    });

    protected prepareSelectedRedButtonSign() {

        this.signRed = new PIXI.Text(this.translator.t.doubleUpSelectedSign, this.signTxt);
        this.signRed.name = this.resid.ScopedResourceName("red-sign");

        this.redSingContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.redSingContainer.width = this.gameContainerSize.Width;
        this.redSingContainer.height = this.gameContainerSize.Height / 8;
        this.redSingContainer.x = 0;
        this.redSingContainer.y = 0;

        let redbtnSignContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        redbtnSignContainer.width = this.signSize.Width;
        redbtnSignContainer.height = this.signSize.Height;
        this.signRed.anchor.set(0.5, 0.5);
        this.signRed.position.set((this.gameContainerSize.Width / 4), this.gameContainerSize.Height / 2.2 + this.cardSize.Width/2)
        redbtnSignContainer.addChild(this.signRed);
        //redbtnSignContainer.x = ((containerSize.width)/4)-(this.signSize.width/2);
        //redbtnSignContainer.y = (containerSize.height/2)+(this.signSize.height/2);
        this.redSingContainer.addChild(redbtnSignContainer);
        this.stage.addChild(this.redSingContainer);
        this.redSingContainer.visible = false;

    }
    protected prepareSelectedBlackButtonSign() {
        this.sign = new PIXI.Text(this.translator.t.doubleUpSelectedSign, this.signTxt);
        this.sign.name = this.resid.ScopedResourceName("black-sign");

        this.blackSingContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.blackSingContainer.width = this.gameContainerSize.Width;
        this.blackSingContainer.height = this.gameContainerSize.Height / 8;
        this.blackSingContainer.x = 0;
        this.blackSingContainer.y = 0;

        let blackbtnSignContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        blackbtnSignContainer.width = this.signSize.Width;
        blackbtnSignContainer.height = this.signSize.Height;
        this.sign.anchor.set(0.5, 0.5);
        this.sign.position.set((this.gameContainerSize.Width / 4) * 3, this.gameContainerSize.Height / 2.2+this.cardSize.Width/2)
        blackbtnSignContainer.addChild(this.sign);
        // blackbtnSignContainer.x = ((this.gameContainer.width)*0.75)-(this.signSize.width/2);
        // blackbtnSignContainer.y = (this.gameContainer.height/2)+(this.signSize.height/2);
        this.blackSingContainer.addChild(blackbtnSignContainer);
        this.stage.addChild(this.blackSingContainer);
        this.blackSingContainer.visible = false;

    }

    /** Por compatibilidad; el slot debe usar ViewPIXIAPP() y añadir al reels container. */
    public View(): HTMLElement {
        return null;
    }

    private isVisible: boolean = false;
    protected Show() {
        if (this.isVisible) return;
        this.history = [];
        this.showRounds(0);
        this.lastPickAction = null;
        if (this.doubleUpContainer) this.doubleUpContainer.visible = true;
        this.isVisible = true;
        if (this.betAndWinContainer) this.betAndWinContainer.visible = true;
    }

    protected async OnEndBegin()
    {

        let endAction: DoubleUpEndAction;


        while (true)
        {
            try
            {
                endAction = await service.onlineApi.doubleUp_End() as DoubleUpEndAction;
                break;
            }
            catch (ex)
            {
                Log.Error(this.logGroup, "Error reporting doubleUp_End: " + ex);
            }

            await Delay(2000);
        }

        this.onEnd(endAction);

    }
    
    protected onEnd(action: DoubleUpEndAction)
    {
        this.UpdatePanel(action);
        if (this.doubleUpContainer) this.doubleUpContainer.visible = false;
        this.betLimitContainerTxt.visible = false;
        this.isVisible = false;
        this.doubleUpEnded?.();
        this.isForcedCollectAll = false;
    }

    public Update() {
        // El ticker del app principal actualiza el stage; no hay app propio.
    }
    // protected redGradContainer: PIXI.Container = null;
    // protected blackGradContainer: PIXI.Container = null;

    // Tamaño base de los botones de juego (RED / BLACK). Más ancho y alto.
    protected gameButtonsSize: Size = new Size(Scale.W(320), Scale.H(170));
    public createRedGradientBtn(): PIXI.Sprite {
        let gradBaseTexture = new PIXI.BaseTexture(new redGradientResource());

        // this.redGradContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        // this.redGradContainer.width = this.gameContainerSize.width;
        // this.redGradContainer.height = this.gameContainerSize.height / 4;
        // this.redGradContainer.x = 0;
        // this.redGradContainer.y = this.gameContainerSize.height / 16;

        gradBaseTexture.setSize(this.gameButtonsSize.Height, this.gameButtonsSize.Width);

        let gradTexture = new PIXI.Texture(gradBaseTexture);

        let redSprite = new PIXI.Sprite(gradTexture);
        redSprite.position.set(this.gameContainerSize.Width / 4 + this.gameButtonsSize.Width / 2, this.gameContainerSize.Height / 2.2 - this.gameButtonsSize.Height / 2+this.cardSize.Width/2);
        redSprite.rotation = Math.PI / 2;

        return redSprite;
        // this.redGradContainer.interactive = true;

        // this.redGradContainer.addListener("pointerdown", (ev: MouseEvent) => {
        //     this.pickRedCard();
        // });

        // this.redGradContainer.addChild(sprite);
        // this.stage.addChild(this.redGradContainer);

    }


    protected randomCardContainer: PIXI.Container = null;
    //protected cardSprite: PIXI.Sprite = null;

    protected  prepareSelectedCardContainer()
    {

        this.randomCardContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        this.randomCardContainer.width = this.cardSize.Width;
        this.randomCardContainer.height = this.cardSize.Height;

        this.gameViewContainer.addChild(this.randomCardContainer);
        this.stage.addChild(this.gameViewContainer);  
        this.randomCardContainer.visible=false;
        

    }

    public selectedCard(cardToShow: Card) {

        this.randomCardContainer.removeChildren();

        let textureCardFrontend = this.cardTextures.get(cardToShow);
        let cardSprite = new PIXI.Sprite(textureCardFrontend);

        cardSprite.x = (this.gameContainerSize.Width / 2) - (this.cardSize.Width / 2);
        cardSprite.y = (this.gameContainerSize.Height * 0.65) - (this.cardSize.Height / 2);

        cardSprite.width = this.cardSize.Width;
        cardSprite.height = this.cardSize.Height;

        this.randomCardContainer.addChild(cardSprite);
        this.lastPickAction.RandomCard = cardToShow;     
        this.randomCardContainer.visible=true;

        
   

        //this.showLastCard(this.action.RandomCard);
    }

    protected hideSelectedCard() {
        this.randomCardContainer.visible = false;
    }

    protected OnRecoverBegin(){
        let lastAction = this.unfinishedState.Actions[this.unfinishedState.Actions.length -1];
        this.doubleUpAction = lastAction

        this.showHistory(lastAction.History);
        if (lastAction.Action == "DoubleUpStartAction")
        {
            this.startGameResponse(lastAction as DoubleUpStartAction);
            //this.stateMachine.ChangeState(DoubleUpStates.StartGame);
        }
        else if (lastAction.Action == "DoubleUpPickAction")
        {
            this.onPickResponse(lastAction as DoubleUpPickAction);
            //this.stateMachine.ChangeState(DoubleUpStates.Playing)
        }
        else if (lastAction.Action == "DoubleUpCollectHalfAction")
        {
            this.onCollectHalfResponse(lastAction as DoubleUpCollectHalfAction)
        }
        else if (lastAction.Action == "DoubleUpCollectAllAction")
        {
            this.onCollectAllResponse(lastAction as DoubleUpCollectAllAction);
        }
    }

    protected picked: number = 0;
    protected unfinishedState:DoubleUpGameState = null;
    protected doubleUpAction: IDoubleUpAction = null;

    protected async OnStart()
    {
        //let startAction = await service.api.doubleUp_Start() as DoubleUpStartAction;
        let startAction = await service.onlineApi.doubleUp_Start() as DoubleUpStartAction;
        this.startGameResponse(startAction);
  
    }
    public Start(unfinishedState: DoubleUpGameState){

        this.unfinishedState = unfinishedState;

        this.robotPlay.DoubleUpStarted(this);

        if (!this.created) this.Create();
        this.Show();

        if (unfinishedState != null)
        {
            this.stateMachine.ChangeState(DoubleUpStates.RecoverState);
            
        }
        else
        {
            this.stateMachine.ChangeState(DoubleUpStates.StartGame);
            
        }
    }

    
    protected OnPlayingBegin()
    {
        this.showHistory(this.history);
        this.creatingAnimation(this.picked);
        this.anim.play();

        this.robotPlay.PlayDoubleUpGame()
    }

    protected OnPlayingEnd()
    {
        this.anim.pause(0);
    }

    protected OnShowPickBegin()
    {
        this.picked = this.lastPickAction.PickSequence;

        //this.onClickRestartCardAnim();
        
        this.audioManager.Track(this.resid.ScopedResourceName("FlipCard")).Play();

        if (this.lastPickAction.PlayerSelectedColor == CardColor.Red)
        {
            this.redSingContainer.visible = true;
        }
        else
        {
            this.blackSingContainer.visible = true;
        }

        this.lastCard.texture = this.cardTextures.get(this.lastPickAction.RandomCard); 
        //this.lastCardContainer.visible = true;

        
        this.showRounds(this.lastPickAction.PickSequence);

        const won: boolean = this.lastPickAction.RandomCardColor == this.lastPickAction.PlayerSelectedColor;
        //service.robotPlay.onWin(won);

        if (won)
        {
            this.winLayoutContainer.visible = true;
            
        }
        else
        {
            if (this.betAndWinContainer) this.betAndWinContainer.visible = false;
            if (this.loseLayoutContainer) this.loseLayoutContainer.visible = true;
        }

        
        this.selectedCard(this.lastPickAction.RandomCard);
        this.UpdatePanel(this.lastPickAction);

        this.nextPickDelay.Start();
    }

    private OnShowPickEnd()
    {
        if (this.redSingContainer) this.redSingContainer.visible = false;
        if (this.blackSingContainer) this.blackSingContainer.visible = false;
        if (this.winLayoutContainer) this.winLayoutContainer.visible = false;
        if (this.randomCardContainer) this.randomCardContainer.visible = false;
        if (this.loseLayoutContainer) this.loseLayoutContainer.visible = false;
        if (this.lastCardContainer) this.lastCardContainer.visible = true;
        this.showHistory(this.lastPickAction.History);


    
        this.nextPickDelay.Stop();
    }

    private NextPickDelayed()
    {
        if (!this.stateMachine.IsState(DoubleUpStates.Playing) &&
            !this.stateMachine.IsState(DoubleUpStates.ShowPick))
        {
            return;
        }

        // no picks left, hide the game
        const won: boolean = this.lastPickAction.RandomCardColor == this.lastPickAction.PlayerSelectedColor;
        if (this.lastPickAction.PickSequence >= this.doubleUpConfiguration.MaxPicks || !won)
        {
            this.stateMachine.ChangeState(DoubleUpStates.EndGame);
        }
        else
        {
            this.stateMachine.ChangeState(DoubleUpStates.Playing);
        }
    }

    private startGameResponse(action: DoubleUpStartAction)
    {
        if(action.ForceGameEnd)
        {
            this.isForcedCollectAll = action.ForceGameEnd;
            this.showBetLimitSign()
            this.stateMachine.ChangeState(DoubleUpStates.CollectAll);
            return;
        }
        if (this.lastCardContainer) this.lastCardContainer.visible = true;
        this.doubleUpAction = action;
        this.picked = action.PickSequence;
        this.showHistory(action.History);
        this.UpdatePanel(action);
        this.stateMachine.ChangeState(DoubleUpStates.Playing);
    }

    public async pickRedCard()
    {
        void this.pick(CardColor.Red)
    }
    public async pickBlackCard()
    {
        void this.pick(CardColor.Black)
    }

    protected async pick(selectedColor: CardColor)
    {   
        if (!this.CanPressButton())
        {
            return;
        }

        

        
        // no picks left, hide the game
        if (this.lastPickAction)
        {
            const won: boolean = this.lastPickAction.RandomCardColor == this.lastPickAction.PlayerSelectedColor;
            if (this.lastPickAction.PickSequence >= this.doubleUpConfiguration.MaxPicks || !won)
            {
                //this.stateMachine.ChangeState(DoubleUpStates.EndGame);
                // wait for timer to expire, and finish the game
                return;
            }
        }

        this.robotPlay.DoubleUpPickAccepted();


        this.stateMachine.ChangeState(DoubleUpStates.WaitResult);
        // DoubleUpPickResult { Action, LastStep }
        let pickResult = await service.onlineApi.doubleUp_Pick(selectedColor) as { Action?: IDoubleUpAction; LastStep?: unknown };
        let pickAction = pickResult?.Action as DoubleUpPickAction;
        this.doubleUpAction = pickAction;
        this.onPickResponse(pickAction);
        console.log("pickAction: ", pickAction);
    }

    protected lastPickAction: DoubleUpPickAction = null;
    protected lastCollectHalfAction: DoubleUpCollectHalfAction = null;
    protected isForcedCollectAll: boolean = false;
    
    private onPickResponse(action: DoubleUpPickAction)
    {
       if (!this.stateMachine.IsState(DoubleUpStates.Playing) &&
           !this.stateMachine.IsState(DoubleUpStates.WaitResult) &&
           !this.stateMachine.IsState(DoubleUpStates.RecoverState))
        {
            return;
        }

        if (action == null)
        {
            throw "WTF action is empty, what did you pick";
        }
        if (action.ForceGameEnd)
        {
            this.isForcedCollectAll = action.ForceGameEnd;
            this.showBetLimitSign();
            this.stateMachine.ChangeState(DoubleUpStates.CollectAll);
            return;
        }

        this.lastPickAction = action;
        this.stateMachine.ChangeState(DoubleUpStates.ShowPick);
    }

    protected CanPressButton(): boolean
    {
        if (!this.stateMachine.IsState(DoubleUpStates.Playing) &&
            !this.stateMachine.IsState(DoubleUpStates.ShowPick) &&
            !this.stateMachine.IsState(DoubleUpStates.ShowCollectHalf))
        {
            return false;
        }

        return true;
    }

    protected CanCollect(): boolean
    {
        if (this.doubleUpAction == null)
        {
            // no previous action, can't collect
            return false;
        }

        if (this.doubleUpAction.Action == "DoubleUpPickAction")
        {
            let pickAction = this.doubleUpAction as DoubleUpPickAction;

            if (pickAction.PickSequence >= this.doubleUpConfiguration.MaxPicks)
            {
                // picked all, can't collect
                return false;
            }

            if (!pickAction.Won)
            {
                // lost, can't collect;
                return false;
            }
        }

        if (this.doubleUpAction.Action == "DoubleUpCollectAllAction")
        {
            // already collected all, can't collect anymore
            return false;
        }

        return true;
    }

    public createBlackGradientBtn():PIXI.Sprite {
        let gradBaseTexture = new PIXI.BaseTexture(new blackGradientResource());

        // this.blackGradContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        // this.blackGradContainer.width = this.gameContainerSize.width;
        // this.blackGradContainer.height = this.gameContainerSize.height;
        // this.blackGradContainer.x = 0;
        // this.blackGradContainer.y = this.gameContainerSize.height / 16;

        gradBaseTexture.setSize(this.gameButtonsSize.Height, this.gameButtonsSize.Width);

        let gradTexture = new PIXI.Texture(gradBaseTexture);

        let blackSprite = new PIXI.Sprite(gradTexture);
        blackSprite.position.set(((this.gameContainerSize.Width * 0.75) + this.gameButtonsSize.Width / 2), (this.gameContainerSize.Height / 2.2 - this.gameButtonsSize.Height / 2+this.cardSize.Width/2));
        blackSprite.rotation = Math.PI / 2;

        return blackSprite;
        //this.blackGradContainer.interactive = true;

        //this.blackGradContainer.addChild(sprite);
        //this.stage.addChild(this.blackGradContainer);

    }
    // Tamaño base de los botones de control (COLLECT / COLLECT HALF). Más ancho y alto.
    protected controlButtonsSize: Size = new Size(Scale.W(380), Scale.H(145));

    public createCollectHalfGradient():PIXI.Sprite {
        let gradBaseTexture = new PIXI.BaseTexture(new controlsBackgroundGradientResource());

        // let halfbetGradContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        // halfbetGradContainer.width = this.gameContainerSize.width;
        // halfbetGradContainer.height = this.gameContainerSize.height / 4;
        // halfbetGradContainer.x = 0;
        // halfbetGradContainer.y = this.gameContainerSize.height / 10;

        gradBaseTexture.setSize(this.controlButtonsSize.Height, this.controlButtonsSize.Width);

        let gradTexture = new PIXI.Texture(gradBaseTexture);

        let sprite = new PIXI.Sprite(gradTexture);
        sprite.position.set(((this.gameContainerSize.Width * 0.75) + this.controlButtonsSize.Width / 2), (this.gameContainerSize.Height / 8) * 5.75 - (this.controlButtonsSize.Height / 2));
        sprite.rotation = Math.PI / 2;

        return sprite;
        // halfbetGradContainer.interactive = true;

        // halfbetGradContainer.addListener("pointerdown", (ev: MouseEvent) => {
        //     this.pickCollectHalf();
        // });

        // halfbetGradContainer.addChild(sprite);
        // this.stage.addChild(halfbetGradContainer);
    }

    public pickCollectHalf()
    {
        if (!this.CanPressButton())
        {
            return;
        }

        if (!this.CanCollect())
        {
            return;
        }

        if (this.doubleUpAction && this.doubleUpAction.Credit.InCents <= 1)
        {
            // can't collect half of 1 cent
            return;
        }

        this.stateMachine.ChangeState(DoubleUpStates.CollectHalf);
        this.robotPlay.DoubleUpPickAccepted();
    }

    protected async onCollectHalfBegin() 
    {
        try
        {
            let result = await service.onlineApi.doubleUp_CollectHalf() as DoubleUpCollectHalfAction;
            this.onCollectHalfResponse(result);
        }
        catch
        {
            this.stateMachine.ChangeState(DoubleUpStates.Playing);
        }
    }


    protected onCollectHalfEnd()
    {
    }

    protected onCollectHalfResponse(action: DoubleUpCollectHalfAction)
    {
        this.doubleUpAction = action;
        this.lastCollectHalfAction = action;

        this.audioManager.Track(this.resid.ScopedResourceName("Collect")).Play();
        this.picked = action.PickSequence;
        this.UpdatePanel(action);

        this.stateMachine.ChangeState(DoubleUpStates.ShowCollectHalf);
    }

    protected onShowCollectHalfBegin()
    {
        this.collecHalfContainerTxt.visible = true;
        this.collectHalfDelay.Start();
    }

    protected onShowCollectHalfEnd()
    {
        this.collecHalfContainerTxt.visible = false;
        this.collectHalfDelay.Stop()
    }

    protected onShowCollectHalfDelayedEnd()
    {
        if (!this.stateMachine.IsState(DoubleUpStates.ShowCollectHalf))
        {
            return;
        }

        this.stateMachine.ChangeState(DoubleUpStates.Playing);

    }

    public pickCollectAll()
    {
        if (!this.CanPressButton())
        {
            return;
        }

        if (!this.CanCollect())
        {
            return;
        }

        this.stateMachine.ChangeState(DoubleUpStates.CollectAll);
        this.robotPlay.DoubleUpPickAccepted();
    }

    protected async onCollectAllBegin()
    {
        let result = await service.onlineApi.doubleUp_CollectAll() as DoubleUpCollectAllAction;
        this.onCollectAllResponse(result)

    }

    protected onCollectAllEnd()
    {
        if (this.collectAllContainerTxt)
            this.collectAllContainerTxt.visible = false;
    }

    protected onCollectAllResponse(action: DoubleUpCollectAllAction)
    {
        this.audioManager.Track(this.resid.ScopedResourceName("Collect")).Play();
        this.UpdatePanel(action);
        this.stateMachine.ChangeState(DoubleUpStates.ShowCollectAll);
    }

    protected onShowCollectAllBegin()
    {
        if (!this.isForcedCollectAll && this.collectAllContainerTxt)
        {
            this.collectAllContainerTxt.visible = true;
        }
        this.collectAllDelay.Start();
    }

    protected onShowCollectAllEnd()
    {
        this.collectAllDelay.Stop();
        if (this.collectAllContainerTxt)
            this.collectAllContainerTxt.visible = false;
    }

    protected onShowCollectAllDelayedEnd()
    {
        this.stateMachine.ChangeState(DoubleUpStates.EndGame);
    }

    public createCollectbtnBackgndGradient(): PIXI.Sprite {
        let gradBaseTexture = new PIXI.BaseTexture(new controlsBackgroundGradientResource());

        // let collectGradContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        // collectGradContainer.width = this.gameContainerSize.width;
        // collectGradContainer.height = this.gameContainerSize.height;
        // collectGradContainer.x = 0;
        // collectGradContainer.y = this.gameContainerSize.height / 10;

        gradBaseTexture.setSize(this.controlButtonsSize.Height, this.controlButtonsSize.Width);

        let gradTexture = new PIXI.Texture(gradBaseTexture);

        let sprite = new PIXI.Sprite(gradTexture);
        sprite.position.set(((this.gameContainerSize.Width / 4) + this.controlButtonsSize.Width / 2), (this.gameContainerSize.Height / 8) * 5.75 - (this.controlButtonsSize.Height / 2));
        sprite.rotation = Math.PI / 2;

        return sprite;
        // collectGradContainer.interactive = true;

        // collectGradContainer.addListener("pointerdown", (ev: MouseEvent) => {
        //     this.pickCollectAll(); 
        // });

        // collectGradContainer.addChild(sprite);
        // this.stage.addChild(collectGradContainer);

    }
    public showGainBackgndGradient() {
        let gainSpace: Size = new Size(Scale.W(500), Scale.H(75));
        let gradBaseTexture = new PIXI.BaseTexture(new gainBackgroundGradientResource());

        let gainGradContainer = new MyPIXIContainer(this.resid.ScopedResourceName("Container"));
        gainGradContainer.width = this.gameContainerSize.Width;
        gainGradContainer.height = this.gameContainerSize.Height;
        gainGradContainer.x = 0;
        gainGradContainer.y = this.gameContainerSize.Height / 10;

        gradBaseTexture.setSize(gainSpace.Height, gainSpace.Width);

        let gradTexture = new PIXI.Texture(gradBaseTexture);

        let sprite = new PIXI.Sprite(gradTexture);
        sprite.position.set(((this.gameContainerSize.Width / 2) + gainSpace.Width / 2), (this.gameContainerSize.Height / 8) * 5.15 - (gainSpace.Height / 2));
        sprite.rotation = Math.PI / 2;

        gainGradContainer.addChild(sprite);
        this.stage.addChild(gainGradContainer);

    }

    //protected lastWin: number = 0;
    private UpdatePanel(action: IDoubleUpAction) {

        console.log(`Action ${action.Action} credit: ${action.Credit.InCents} collected: ${action.Collected.InCents} creditend: ${action.CreditEnd.InCents}`)
        this.UpdatePaytable();
        this.OnWinChanged2?.(action.Credit.InCents);
        this.OnCreditChanged2?.(action.CreditEnd.InCents);
    }

    private UpdatePaytable()
    {
        if (this.isForcedCollectAll)
        {
            return
        }
        if (this.betAndWinTxt) {
            this.betAndWinTxt.text = this.translator.t.doubleUpBetAndWinTxt(
                service.moneyFormatter.FormatCurrency(this.doubleUpAction.Credit?.InCents),
                service.moneyFormatter.FormatCurrency(this.doubleUpAction.Credit?.InCents*2),
            );
        }
    }
    // protected UpdateNumbersOnPanel(credit: number) {
    //     this.money.text = service.moneyFormatter.FormatCurrency(credit);
    // }

    public doubleUpEnded: doubleUpGameStateChangedCallback = null;


    public OnWinChanged2: (win: number) => void;
    public OnCreditChanged2: (credit: number) => void;
}

class redGradientResource extends PIXI.Resource {
    constructor() {
        let buttonSize: Size = new Size(Scale.W(250), Scale.H(150));
        super(buttonSize.Width, buttonSize.Height);
    }

    public upload(renderer, baseTexture, glTexture) {
        let containerSize: Size = new Size(Scale.W(1920), Scale.H(1080));
        let buttonSize: Size = new Size(Scale.W(250), Scale.H(75));

        // temporary canvas, we dont need it after texture is uploaded to GPU
        let canvas = document.createElement('canvas');
        canvas.width = containerSize.Width;
        canvas.height = containerSize.Height;

        let ctx = canvas.getContext('2d');

        let grd = ctx.createLinearGradient(0, 0, containerSize.Width, 0);
        grd.addColorStop(0.1, 'rgb(89, 3, 3)');
        grd.addColorStop(0.5, 'rgb(140,4,4)');
        grd.addColorStop(0.9, 'rgb(89, 3, 3)');


        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, containerSize.Width, containerSize.Height);

        glTexture.width = containerSize.Width;
        glTexture.height = containerSize.Height;

        let { gl } = renderer;
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, baseTexture.premultiplyAlpha);
        gl.texImage2D(baseTexture.target, 0, baseTexture.format, baseTexture.format, baseTexture.type, canvas);

        return true;
    }
}

class blackGradientResource extends PIXI.Resource {
    constructor() {
        let buttonSize: Size = new Size(Scale.W(300), Scale.H(75));
        super(buttonSize.Width, buttonSize.Height);
    }

    public upload(renderer, baseTexture, glTexture) {
        let containerSize: Size = new Size(Scale.W(1920), Scale.H(1080));
        let buttonSize: Size = new Size(Scale.W(150), Scale.H(75));

        // temporary canvas, we dont need it after texture is uploaded to GPU
        let canvas = document.createElement('canvas');
        canvas.width = containerSize.Width;
        canvas.height = containerSize.Height;

        let ctx = canvas.getContext('2d');

        let grd = ctx.createLinearGradient(0, 0, containerSize.Width, 0);
        grd.addColorStop(0.1, 'rgb(0,0,0)');
        grd.addColorStop(0.5, 'rgb(40, 40, 40)');
        grd.addColorStop(0.9, 'rgb(0,0,0)');


        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, containerSize.Width, containerSize.Height);

        glTexture.width = containerSize.Width;
        glTexture.height = containerSize.Height;

        let { gl } = renderer;
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, baseTexture.premultiplyAlpha);
        gl.texImage2D(baseTexture.target, 0, baseTexture.format, baseTexture.format, baseTexture.type, canvas);

        return true;
    }
    
}

class controlsBackgroundGradientResource extends PIXI.Resource {
    constructor() {
        let buttonSize: Size = new Size(Scale.W(300), Scale.H(75));
        super(buttonSize.Width, buttonSize.Height);
    }

    public upload(renderer, baseTexture, glTexture) {
        let containerSize: Size = new Size(Scale.W(1920), Scale.H(1080));

        // temporary canvas, we dont need it after texture is uploaded to GPU
        let canvas = document.createElement('canvas');
        canvas.width = containerSize.Width;
        canvas.height = containerSize.Height;

        let ctx = canvas.getContext('2d');

        let grd = ctx.createLinearGradient(0, 0, containerSize.Width, 0);
        grd.addColorStop(0.1, 'rgb(60,60,60)');
        grd.addColorStop(0.5, 'rgb(0, 0, 0)');
        grd.addColorStop(0.9, 'rgb(60,60,60)');


        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, containerSize.Width, containerSize.Height);

        glTexture.width = containerSize.Width;
        glTexture.height = containerSize.Height;

        let { gl } = renderer;
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, baseTexture.premultiplyAlpha);
        gl.texImage2D(baseTexture.target, 0, baseTexture.format, baseTexture.format, baseTexture.type, canvas);

        return true;
    }

}
export type doubleUpGameStateChangedCallback = () => void;
class gainBackgroundGradientResource extends PIXI.Resource {
    constructor() {
        let buttonSize: Size = new Size(Scale.W(500), Scale.H(75));
        super(buttonSize.Width, buttonSize.Height);
    }

    public upload(renderer, baseTexture, glTexture) {
        let containerSize: Size = new Size(Scale.W(1920), Scale.H(1080));


        // temporary canvas, we dont need it after texture is uploaded to GPU
        let canvas = document.createElement('canvas');
        canvas.width = containerSize.Width;
        canvas.height = containerSize.Height;

        let ctx = canvas.getContext('2d');

        let grd = ctx.createLinearGradient(0, 0, containerSize.Width, 0);
        grd.addColorStop(0.1, 'rgb(60,60,60)');
        grd.addColorStop(0.5, 'rgb(0, 0, 0)');
        grd.addColorStop(0.9, 'rgb(60,60,60)');


        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, containerSize.Width, containerSize.Height);

        glTexture.width = containerSize.Width;
        glTexture.height = containerSize.Height;

        let { gl } = renderer;
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, baseTexture.premultiplyAlpha);
        gl.texImage2D(baseTexture.target, 0, baseTexture.format, baseTexture.format, baseTexture.type, canvas);

        return true;
    }
}

